import React, { useEffect, useState, useRef } from 'react';
import { AlertCircleIcon, CheckCircleIcon, FilterIcon, ShieldIcon, ChevronUpIcon, ChevronDownIcon, HelpCircleIcon } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
interface DomainTabProps {
  brandId: number;
}
type SortField = 'domain' | 'riskLevel' | 'status' | 'dateFound';
type SortDirection = 'asc' | 'desc';
export const DomainTab: React.FC<DomainTabProps> = ({
  brandId
}) => {
  const [showFilterMenu, setShowFilterMenu] = useState(false);
  const [sortField, setSortField] = useState<SortField>('domain');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const filterRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // Mock domains data
  const [domains, setDomains] = useState([{
    id: 1,
    domain: 'acme-inc.net',
    riskLevel: 'high',
    status: 'active',
    dateFound: '2023-06-15'
  }, {
    id: 2,
    domain: 'acme-inc-official.com',
    riskLevel: 'medium',
    status: 'active',
    dateFound: '2023-07-02'
  }, {
    id: 3,
    domain: 'acmeinc.co',
    riskLevel: 'low',
    status: 'monitoring',
    dateFound: '2023-07-10'
  }, {
    id: 4,
    domain: 'acme.shop',
    riskLevel: 'high',
    status: 'blocked',
    dateFound: '2023-08-05'
  }, {
    id: 5,
    domain: 'acme-support.net',
    riskLevel: 'medium',
    status: 'monitoring',
    dateFound: '2023-08-12'
  }, {
    id: 6,
    domain: 'acmeinc-store.com',
    riskLevel: 'high',
    status: 'active',
    dateFound: '2023-08-15'
  }, {
    id: 7,
    domain: 'acmeincorporated.com',
    riskLevel: 'low',
    status: 'monitoring',
    dateFound: '2023-08-18'
  }, {
    id: 8,
    domain: 'acme-secure.net',
    riskLevel: 'medium',
    status: 'blocked',
    dateFound: '2023-08-20'
  }, {
    id: 9,
    domain: 'acmeinc-login.com',
    riskLevel: 'high',
    status: 'active',
    dateFound: '2023-08-22'
  }, {
    id: 10,
    domain: 'myacmeinc.com',
    riskLevel: 'low',
    status: 'monitoring',
    dateFound: '2023-08-25'
  }]);
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    const sortedDomains = [...domains].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];
      const sortOrder = sortDirection === 'asc' ? 1 : -1;
      if (field === 'dateFound') {
        return (new Date(aValue).getTime() - new Date(bValue).getTime()) * sortOrder;
      }
      return aValue.localeCompare(bValue) * sortOrder;
    });
    setDomains(sortedDomains);
  };
  const SortHeader: React.FC<{
    field: SortField;
    label: string;
  }> = ({
    field,
    label
  }) => <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100" onClick={() => handleSort(field)} data-id="element-105">
      <div className="flex items-center space-x-1" data-id="element-106">
        <span data-id="element-107">{label}</span>
        <div className="flex flex-col" data-id="element-108">
          <ChevronUpIcon className={`h-3 w-3 ${sortField === field && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-109" />
          <ChevronDownIcon className={`h-3 w-3 ${sortField === field && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-110" />
        </div>
      </div>
    </th>;
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <div className="space-y-6" data-id="element-111">
      <div className="bg-white shadow-sm rounded-lg p-6" data-id="element-112">
        <div className="flex items-center justify-between mb-6" data-id="element-113">
          <div className="flex items-center" data-id="element-114">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-115">
              Domain Monitor
            </h2>
            <div className="relative ml-2 group" data-id="element-116">
              <HelpCircleIcon className="h-4 w-4 text-gray-400 hover:text-gray-500 cursor-help" data-id="element-117" />
              <div className="hidden group-hover:block absolute left-1/2 transform -translate-x-1/2 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10" data-id="element-118">
                Domain Monitor automatically scans for new domains that match
                keywords similar to your brand domain.
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full w-2 h-2 bg-gray-900 rotate-45" data-id="element-119"></div>
              </div>
            </div>
          </div>
          <div className="flex space-x-2" data-id="element-120">
            <div className="relative" ref={filterRef} data-id="element-121">
              <button onClick={() => setShowFilterMenu(!showFilterMenu)} className="px-3 py-1.5 bg-white border border-gray-300 rounded text-sm text-gray-700 flex items-center" data-id="element-122">
                <FilterIcon className="h-4 w-4 mr-2" data-id="element-123" />
                Filter
              </button>
              {showFilterMenu && <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200" data-id="element-124">
                  <div className="py-1" data-id="element-125">
                    <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-126">
                      Safe Domains
                    </button>
                    <button className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-127">
                      Risky Domains
                    </button>
                  </div>
                </div>}
            </div>
            <button className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700" data-id="element-128">
              Run Scan
            </button>
          </div>
        </div>
        <div className="overflow-x-auto" data-id="element-129">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-130">
            <thead className="bg-gray-50" data-id="element-131">
              <tr data-id="element-132">
                <SortHeader field="domain" label="Domain" data-id="element-133" />
                <SortHeader field="riskLevel" label="Risk Level" data-id="element-134" />
                <SortHeader field="status" label="Status" data-id="element-135" />
                <SortHeader field="dateFound" label="Date Found" data-id="element-136" />
                <th scope="col" className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-137"></th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-138">
              {domains.map(domain => <tr key={domain.id} className="hover:bg-gray-50" data-id="element-139">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-140">
                    <Link to={`/brand/${brandId}/domains/${domain.id}`} className="text-blue-600 hover:text-blue-900" data-id="element-141">
                      {domain.domain}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-142">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${domain.riskLevel === 'high' ? 'bg-red-100 text-red-800' : domain.riskLevel === 'medium' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`} data-id="element-143">
                      {domain.riskLevel === 'high' ? <AlertCircleIcon className="mr-1 h-3 w-3" data-id="element-144" /> : domain.riskLevel === 'medium' ? <AlertCircleIcon className="mr-1 h-3 w-3" data-id="element-145" /> : <CheckCircleIcon className="mr-1 h-3 w-3" data-id="element-146" />}
                      {domain.riskLevel.charAt(0).toUpperCase() + domain.riskLevel.slice(1)}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-147">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${domain.status === 'active' ? 'bg-blue-100 text-blue-800' : domain.status === 'blocked' ? 'bg-gray-100 text-gray-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-148">
                      {domain.status.charAt(0).toUpperCase() + domain.status.slice(1)}
                    </span>
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-149">
                    {domain.dateFound}
                  </td>
                  <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-150">
                    <button onClick={() => navigate(`/brand/${brandId}/domains/${domain.id}`)} className="text-blue-600 hover:text-blue-900 text-sm" data-id="element-151">
                      View Details
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
};